import classNames from 'classnames';
import { find, difference } from 'lodash';
import PropTypes from 'prop-types';
import { createRef, Component, cloneElement } from 'react';

import { Button } from '@hivebrite/design-system/Button';
import { mobileDeviceFromUserAgent } from '@hivebrite/utils';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import Loader from 'hb-react/shared/components/Loader';
import UserSearch from 'hb-react/shared/components/UserSearch';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import TargetingFilters from './TargetingFilters';
import UserList from './UserList';

const maxTargetedUsersTxt = (nb, max) =>
  I18n.t('web.targeting_module.users_selected', {
    count: nb,
    nb,
    max,
  });

const initialUserTargetingState = {
  page: 1,
  selectedUsersPage: 1,
  can_fetch: true,
  can_fetch_more_selected_users: true,
  users: [],
  user_ids: [],
  total_users: 0,
  selectedUsers: [],
  selectedUserIds: [],
  select_all: false,
  mobile_step: 1,
  loading: true,
};

class UserTargeting extends Component {
  contentPreviewRef = createRef();

  constructor(props) {
    super(props);

    this.state = {
      ...initialUserTargetingState,
      isOpen: props.open,
    };
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.defaultSelectedUsers.length > 0) {
      this.selectUsers(nextProps.defaultSelectedUsers);
    } else {
      this.setState({
        selectedUserIds: [],
        selectedUsers: [],
      });
    }
  }

  validate = () => {
    const { onValidateSelection } = this.props;

    if (onValidateSelection) {
      onValidateSelection(this.state.selectedUsers, this.state.selectedUserIds);
    }
    this.handleClose();
  };

  cancel = () => {
    const { onCancelSelection } = this.props;

    this.setState(initialUserTargetingState, () => {
      if (onCancelSelection) {
        onCancelSelection();
      }
      this.handleClose();
    });
  };

  selectUsers = (selectedUsers) =>
    this.setState({
      selectedUserIds: selectedUsers.map((u) => u.id),
      selectedUsers,
    });

  handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    this.triggerSearch(true);
  };

  triggerSearch = (reset = false) => {
    const { defaultQuery, perPage } = this.props;
    const { isOpen } = this.state;

    const prepareAndFetch = async () => {
      if (this.state.can_fetch) {
        this.setState({ can_fetch: false });
        if (
          this.state.users.length < this.state.total_users ||
          this.state.total_users === 0
        ) {
          const query = {};

          Object.assign(
            query,
            defaultQuery,
            // eslint-disable-next-line jquery/no-ajax, no-undef
            $('.js-targeting-form').serializeJSON().query,
            // eslint-disable-next-line jquery/no-ajax, no-undef
            $(this.searchTextForm).serializeJSON().query,
          );

          const data = {
            page: this.state.page,
            per_page: perPage,
            query,
            query_options: {
              user_ids: this.state.page === 1,
            },
          };

          await axios
            .post(Routes.targeting_users_path(), data)
            .then(({ data }) => {
              this.setState({ loading: false });

              if (isOpen) {
                this.setState((prevState) => ({
                  page: (prevState.page += 1),
                  can_fetch: true,
                  can_fetch_more_selected_users: true,
                  users: [...prevState.users, ...data.users],
                  user_ids: data.user_ids,
                  total_users: data.total_items,
                }));
              }
            });
        }
      }
    };

    if (reset) {
      this.setState(
        {
          page: 1,
          can_fetch: true,
          users: [],
          user_ids: [],
          total_users: 0,
          loading: true,
        },
        prepareAndFetch,
      );
    } else {
      prepareAndFetch();
    }
  };

  handleRefreshSelectedUsers = async () => {
    const { isOpen, selectedUsers } = this.state;

    if (
      this.state.can_fetch_more_selected_users &&
      this.state.selectedUserIds.length > 0
    ) {
      this.setState({ can_fetch_more_selected_users: false });
      if (this.state.selectedUsers.length < this.state.selectedUserIds.length) {
        const data = {
          page: this.state.selectedUsersPage,
          per_page: 100,
          query: {
            user_ids: this.state.selectedUserIds,
          },
        };

        await axios
          .post(Routes.targeting_users_path(), data)
          .then(({ data }) => {
            if (isOpen) {
              let newSelectedUsers = [...selectedUsers, ...data.users];

              if (this.state.selectedUsersPage === 1) {
                newSelectedUsers = data.users;
              }
              this.setState((prevState) => ({
                selectedUsersPage: (prevState.selectedUsersPage += 1),
                can_fetch_more_selected_users: true,
                selectedUsers: newSelectedUsers,
              }));
            }
          });
      }
    }
  };

  canSelectMoreUsers = () => {
    const { maxTargetedUsers } = this.props;
    const { selectedUserIds } = this.state;

    return Boolean(selectedUserIds.length < maxTargetedUsers);
  };

  handleSelectUserClick = (user) => {
    const isMobile = mobileDeviceFromUserAgent.any();

    const userNotExist = find(
      this.state.selectedUserIds,
      (id) => user.id === id,
    );

    let { selectedUsers } = this.state;
    let { selectedUserIds } = this.state;

    if (!userNotExist && this.canSelectMoreUsers()) {
      if (!isMobile) {
        selectedUsers = [user, ...selectedUsers];
      }
      selectedUserIds = [user.id, ...selectedUserIds];
    } else {
      if (!isMobile) {
        selectedUsers = selectedUsers.filter((u) => u.id !== user.id);
      }
      selectedUserIds = selectedUserIds.filter((userId) => userId !== user.id);

      if (this.props.onRemoveUser) {
        this.props.onRemoveUser([user]);
      }
    }

    this.setState({
      selectedUsers,
      selectedUserIds,
    });
  };

  handleSelectAllClick = (selectAll) => {
    const isMobile = mobileDeviceFromUserAgent.any();

    const { selectedUsers, selectedUserIds } = this.state;

    let newSelectedUsers = selectedUsers;
    let newSelectedUserIds = selectedUserIds;

    if (selectAll && this.canSelectMoreUsers()) {
      if (!isMobile) {
        newSelectedUsers = difference(this.state.users, newSelectedUsers);
        newSelectedUsers = [...this.state.selectedUsers, ...newSelectedUsers];
      }

      newSelectedUserIds = difference(this.state.user_ids, newSelectedUserIds);
      newSelectedUserIds = [
        ...this.state.selectedUserIds,
        ...newSelectedUserIds,
      ];
    } else {
      [newSelectedUsers, newSelectedUserIds] = [[], []];
    }

    this.setState(
      {
        selectedUsers: newSelectedUsers.slice(0, this.props.maxTargetedUsers),
        selectedUserIds: newSelectedUserIds.slice(
          0,
          this.props.maxTargetedUsers,
        ),
        selectedUsersPage: 1,
      },
      () => {
        this.handleRefreshSelectedUsers();
      },
    );
  };

  updateMobileStep =
    (action = null) =>
    () => {
      if (action === 'next') {
        this.setState((prevState) => ({
          mobile_step: ++prevState.mobile_step,
        }));
      } else if (action === 'previous') {
        this.setState((prevState) => ({
          mobile_step: --prevState.mobile_step,
        }));
      }
    };

  renderHeader = () => {
    const isMobile = mobileDeviceFromUserAgent.any();

    if (isMobile) {
      switch (this.state.mobile_step) {
        case 1:
          return (
            <div className="f-size--1-2">
              {`${I18n.t('global.users', {
                count: this.state.total_users,
              })}: ${this.state.total_users}`}
            </div>
          );

        case 2:
          return (
            <div className="f-size--1-2">
              {maxTargetedUsersTxt(
                this.state.selectedUserIds.length,
                this.props.maxTargetedUsers,
              )}
            </div>
          );

        default:
      }
    } else {
      return (
        <form
          ref={(ref) => (this.searchTextForm = ref)}
          onSubmit={this.handleSubmit}
          data-testid="search-for-user"
        >
          <UserSearch
            type="targeting"
            onSelectUser={this.handleSelectUserClick}
            onSeeAll={this.handleSubmit}
            focusOnRender
          />
        </form>
      );
    }
  };

  renderFooter = () => {
    const isMobile = mobileDeviceFromUserAgent.any();

    const cancelButton = (
      <Button onClick={this.cancel} variant="secondary">
        {I18n.t('actions.cancel')}
      </Button>
    );
    const validateButton = (
      <Button
        ml={3}
        onClick={this.validate}
        dataTestId="user-targeting-confirm"
      >
        {I18n.t('web.targeting_module.validate')}
      </Button>
    );

    if (isMobile) {
      switch (this.state.mobile_step) {
        case 1:
          return (
            <div>
              {cancelButton}
              <Button ml={3} onClick={this.updateMobileStep('next')}>
                {I18n.t('web.targeting_module.see_results')}
              </Button>
            </div>
          );

        case 2:
          return (
            <div className="flexbox">
              <div className="flexbox__item text--left soft-half--right">
                <a
                  href="#"
                  className="f-size--1-2 color-dark-gray"
                  onClick={this.updateMobileStep('previous')}
                >
                  <i className="icon-chevron-left push-quarter--right" />
                  {I18n.t('global.filters')}
                </a>
              </div>
              <div className="flexbox__item">
                {cancelButton}
                {validateButton}
              </div>
            </div>
          );

        default:
      }
    } else {
      return (
        <div>
          {cancelButton}
          {validateButton}
        </div>
      );
    }
  };

  handleClose = () => {
    const { onClose } = this.props;

    this.setState({ isOpen: false });
    onClose?.();
  };

  handleOpen = () => {
    const { onOpen } = this.props;

    this.setState({ isOpen: true });
    onOpen?.();
  };

  render() {
    const isMobile = mobileDeviceFromUserAgent.any();

    const { defaultSelectedUsers, maxTargetedUsers, openByClickOn } =
      this.props;
    const {
      isOpen,
      loading,
      mobile_step: mobileStep,
      selectedUserIds,
      selectedUsers,
      total_users: totalUsers,
      users,
    } = this.state;

    const defaultSelectedUsersAreFrozen = Object.isFrozen(defaultSelectedUsers);
    const defaultSelectedUsersIds = defaultSelectedUsers.map((u) => u.id);

    return (
      <>
        {cloneElement(openByClickOn, {
          onClick: this.handleOpen,
        })}

        {isOpen && (
          <ModalWithTemplate
            dataTestId="content-preview-modal"
            footer={
              <>
                {maxTargetedUsers === selectedUserIds.length && (
                  <div className="flexbox__item">
                    {I18n.t('web.targeting_module.max_targeted_users_reached')}
                  </div>
                )}

                <div className="flexbox__item text--right">
                  {this.renderFooter()}
                </div>
              </>
            }
            ref={this.contentPreviewRef}
            onCloseModal={this.handleClose}
            isOpen={isOpen}
            isFixed={!isMobile}
            isSticky={isMobile}
            pb={0}
            width="large"
            header={I18n.t('web.targeting_module.modal_title')}
            screenReadersCloseButtonText={s__(
              'Accessibility|Close notify people window',
            )}
            shouldNotifyMobileApp={false}
          >
            {/* Force the Form to not use custom theming on Inputs since it's supra old & soon replaced */}
            <ThemeProvider
              theme="fo"
              options={{
                customNetworkTheming: {
                  ...AlumniConnect.currentNetwork?.theming,
                  fo: {
                    ...AlumniConnect.currentNetwork?.theming?.fo,
                    fo_inputs_background_color: null,
                    fo_inputs_outline_color: null,
                    fo_inputs_font_brightness: null,
                  },
                },
              }}
            >
              <div style={{ height: '100%' }}>
                <div
                  className="bg-white"
                  style={{ height: '100%', color: 'rgb(5, 5, 5)' }}
                >
                  <div
                    className="grid user-targeting__container"
                    // eslint-disable-next-line react/no-unused-class-component-methods
                    ref={(ref) => (this.userTargetingContainer = ref)}
                  >
                    <div
                      className={classNames(
                        'grid__item bordered--right height--100',
                        {
                          'one-whole': isMobile,
                          'three-quarters': !isMobile,
                        },
                      )}
                    >
                      <div
                        // eslint-disable-next-line react/no-unused-class-component-methods
                        ref={(ref) => (this.headerSearch = ref)}
                        className="soft-half bordered--bottom user-targeting__header-search"
                      >
                        {this.renderHeader()}
                      </div>

                      <div className="height--100 user-targeting__form">
                        <div className="grid grid--full soft-half--left portable--hard user-targeting__left-container">
                          {(!isMobile || (isMobile && mobileStep === 1)) && (
                            <div
                              className={classNames(
                                'grid__item soft-half--top soft-half--right user-targeting__filters scrollable-area',
                                {
                                  'one-whole soft-half': isMobile,
                                  'one-half': !isMobile,
                                },
                              )}
                            >
                              <TargetingFilters
                                triggerSearch={this.triggerSearch}
                                searchOnInit={!users.length}
                              />
                            </div>
                          )}

                          {(!isMobile || (isMobile && mobileStep === 2)) && (
                            <div
                              className={classNames(
                                'grid__item bordered--left soft-quarter--left height--100 relative scrollable-area',
                                {
                                  'one-whole': isMobile,
                                  'one-half': !isMobile,
                                },
                              )}
                            >
                              {loading ? (
                                <Loader fillParent />
                              ) : (
                                <UserList
                                  key="display-list"
                                  classes="js-main-user-targeting-list"
                                  users={users}
                                  defaultSelectedUsersAreFrozen={
                                    defaultSelectedUsersAreFrozen
                                  }
                                  defaultSelectedUsersIds={
                                    defaultSelectedUsersIds
                                  }
                                  total_users={totalUsers}
                                  selected_user_ids={selectedUserIds}
                                  handleSelectUserClick={
                                    this.handleSelectUserClick
                                  }
                                  handleSelectAllClick={
                                    this.handleSelectAllClick
                                  }
                                  triggerSearch={this.triggerSearch}
                                  can_select_user={this.canSelectMoreUsers()}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {!isMobile && (
                      <div className="grid__item one-quarter soft-half height--100 scrollable-area">
                        <div className="weight--semibold">
                          {maxTargetedUsersTxt(
                            selectedUserIds.length,
                            maxTargetedUsers,
                          )}
                        </div>

                        <UserList
                          key="selected-list"
                          classes="selected-user-list"
                          type="selected"
                          users={selectedUsers}
                          defaultSelectedUsersAreFrozen={
                            defaultSelectedUsersAreFrozen
                          }
                          defaultSelectedUsersIds={defaultSelectedUsersIds}
                          selected_user_ids={selectedUserIds}
                          handleSelectUserClick={this.handleSelectUserClick}
                          handleRefreshSelectedUsers={
                            this.handleRefreshSelectedUsers
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ThemeProvider>
          </ModalWithTemplate>
        )}
      </>
    );
  }
}

UserTargeting.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  defaultQuery: PropTypes.object,
  maxTargetedUsers: PropTypes.number,
  perPage: PropTypes.number,
  onValidateSelection: PropTypes.func,
  onCancelSelection: PropTypes.func,
  onRemoveUser: PropTypes.func,
  defaultSelectedUsers: PropTypes.array, // Array can be frozen using Object.freeze
  openByClickOn: PropTypes.element,
};

UserTargeting.defaultProps = {
  open: false,
  defaultQuery: {},
  maxTargetedUsers: 250,
  perPage: 50,
  defaultSelectedUsers: [],
  onRemoveUser: null,
};

export default UserTargeting;
